import { useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Building2, Mail, Lock, HelpCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import CookieBanner from '@/components/CookieBanner';

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, loading: authLoading } = useAuth();

  const from = location.state?.from?.pathname || '/dashboard';

  // Redirect if already authenticated
  if (!authLoading && isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Login effettuato con successo!');
      navigate(from, { replace: true });
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Errore durante il login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Login Form with Footer */}
      <div className="flex-1 flex flex-col">
        {/* Top Header with FAQ and Support */}
        <header className="bg-white border-b border-stone-200 px-4 py-3">
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/faq')}
              className="text-stone-600 hover:text-slate-900"
            >
              <HelpCircle className="h-4 w-4 mr-2" />
              FAQ
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.location.href = 'mailto:support.app@artegosrls.it'}
              className="text-stone-600 hover:text-slate-900"
            >
              <Mail className="h-4 w-4 mr-2" />
              Supporto
            </Button>
          </div>
        </header>

        {/* Main Login Content */}
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-md space-y-8">
            {/* Logo and Title - Redesigned */}
            <div className="text-center space-y-4">
              {/* ARTEGO.APP with small grid icon (no ARTEGO text) */}
              <div className="flex items-center justify-center gap-2">
                <img
                  src="/assets/artego-icon.svg"
                  alt="Artego Icon"
                  className="h-8 w-auto"
                  data-testid="login-logo"
                />
                <span className="font-heading text-xl font-bold text-slate-900 tracking-tight">
                  ARTEGO.APP
                </span>
              </div>
              
              {/* Amministro Condomini with larger building icon (black bg, white icon) */}
              <div className="flex items-center justify-center gap-3">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-md bg-slate-900">
                  <Building2 className="h-7 w-7 text-white" />
                </div>
                <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight">
                  Amministro Condomini
                </h1>
              </div>
              
              <p className="text-base text-stone-600">
                Sistema di gestione condominiale
              </p>
            </div>

            {/* Login Form */}
            <form onSubmit={handleLogin} className="space-y-6" data-testid="login-form">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-slate-900">
                    Email
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-stone-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="tuo@email.it"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      data-testid="email-input"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium text-slate-900">
                    Password
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-stone-400" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10"
                      data-testid="password-input"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    className="rounded border-stone-300 text-slate-900 focus:ring-slate-900"
                  />
                  <span className="text-sm text-stone-600">Ricordami</span>
                </label>
                <button
                  type="button"
                  className="text-sm font-medium text-orange-800 hover:text-orange-900"
                  data-testid="forgot-password-link"
                  onClick={() => navigate('/forgot-password')}
                >
                  Se hai dimenticato la password, clicca qui
                </button>
              </div>

              <Button
                type="submit"
                className="w-full bg-slate-900 text-white hover:bg-slate-800 shadow-sm rounded-md py-3 font-medium transition-all active:scale-95"
                data-testid="login-submit-button"
                disabled={loading}
              >
                {loading ? 'Accesso...' : 'Accedi'}
              </Button>
            </form>

            {/* Register Link */}
            <div className="text-center">
              <p className="text-sm text-stone-600">
                Non hai un account?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/register')}
                  className="font-medium text-orange-800 hover:text-orange-900"
                  data-testid="register-link"
                >
                  Registrati
                </button>
              </p>
            </div>
          </div>
        </div>

        {/* Footer - Compact, only in left side */}
        <footer className="border-t border-stone-200 bg-stone-50 py-3 px-4">
          {/* Single row: Logo + Contacts + Social */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-4">
              <img
                src="/assets/artego-symbol.png"
                alt="Artego SRLS"
                className="h-8 w-auto"
              />
              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-stone-600">
                <span className="font-medium text-stone-700">Artego SRLS</span>
                <span className="hidden sm:inline">•</span>
                <span className="hidden sm:inline">Via M.se di Villabianca, 114 - Palermo</span>
                <span className="hidden md:inline">•</span>
                <a href="tel:+393520498295" className="hidden md:inline hover:text-slate-900">+39 3520498295</a>
                <span className="hidden lg:inline">•</span>
                <a href="mailto:info@artegosrls.it" className="hidden lg:inline hover:text-slate-900">info@artegosrls.it</a>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <a href="https://www.artegosrls.it" target="_blank" rel="noopener noreferrer" className="text-xs text-stone-500 hover:text-slate-900 font-medium hidden sm:inline">
                www.artegosrls.it
              </a>
              <div className="flex items-center gap-2">
                <a href="https://www.facebook.com/artegosrls" target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-blue-600 transition-colors" aria-label="Facebook">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
                <a href="https://www.instagram.com/artegosrls" target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-pink-600 transition-colors" aria-label="Instagram">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                </a>
                <a href="https://wa.me/393520498295" target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-green-600 transition-colors" aria-label="WhatsApp">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                </a>
                <a href="https://www.youtube.com/@artegosrls" target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-red-600 transition-colors" aria-label="YouTube">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>

      {/* Right Side - Image (Hidden on mobile) */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1712763505655-1077b86cea59?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Modern Italian Apartment Building"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-12 pb-16 text-white">
          <h2 className="font-heading text-4xl font-bold mb-4 drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]" style={{textShadow: '0 2px 8px rgba(0,0,0,0.6)'}}>
            Gestione condominiale professionale
          </h2>
          <p className="text-xl opacity-95 drop-shadow-[0_1px_3px_rgba(0,0,0,0.5)]" style={{textShadow: '0 1px 6px rgba(0,0,0,0.5)'}}>
            Sistema completo per amministratori, contabili e condomini
          </p>
        </div>
      </div>

      {/* Cookie Banner */}
      <CookieBanner />
    </div>
  );
};
